import React, { useState } from 'react';
import {
  PageHeader,
  Card,
  Button,
  Space,
  Typography,
  Result,
  Divider,
  message,
} from 'antd';
import {
  DownloadOutlined,
  TeamOutlined,
  WifiOutlined,
  CloudDownloadOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import { handleApiError, MyBreadcrumb } from 'utils';
import { branchnetworkStatsService } from 'services/branchnetwork';
import { wifiusersSuperAdminSearchService } from 'services/wifiusers';
import Auth from 'auth';
import Papa from 'papaparse';

const { Text } = Typography;

function downloadCsv(rows, filename) {
  const csv = Papa.unparse(rows);
  const blob = new Blob(['\uFEFF' + csv], {
    type: 'text/csv;charset=utf-8;',
  });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function todayStamp() {
  return new Date().toISOString().split('T')[0];
}

function resolveWifiBranchName(user) {
  if (user.branchcustomer?.customername) {
    return user.branchcustomer.customername;
  }
  if (user.wifigroup?.branchcustomer?.customername) {
    return user.wifigroup.branchcustomer.customername;
  }
  if (user.branchnetwork_name) {
    return user.branchnetwork_name;
  }
  if (user.company?.company_name && user.branch?.branch_name) {
    return `${user.company.company_name} - ${user.branch.branch_name}`;
  }
  return '';
}

async function fetchAllWifiUsers() {
  const pageSize = 1000;
  let page = 1;
  let allDocs = [];
  let totalPages = 1;

  do {
    const response = await wifiusersSuperAdminSearchService('', {
      page,
      limit: pageSize,
    });
    const result = response.data.result || {};
    const docs = result.docs || [];
    allDocs = allDocs.concat(docs);
    totalPages = result.totalPages || 1;
    page += 1;
  } while (page <= totalPages);

  return allDocs;
}

const exportRowStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 24,
  flexWrap: 'wrap',
};

export default function SuperAdminDataExport() {
  const isSuperAdmin = Number(Auth.getRole()) === 10;
  const [exportingClients, setExportingClients] = useState(false);
  const [exportingWifiUsers, setExportingWifiUsers] = useState(false);
  const [lastExport, setLastExport] = useState(null);

  const exportAllBranchClients = async () => {
    setExportingClients(true);
    try {
      const response = await branchnetworkStatsService();
      const networks = response.data.items || [];
      const rows = [];

      networks.forEach((network) => {
        const companyName = network.company?.company_name || '';
        const branchName = network.branch?.branch_name || '';
        const networkLabel = `${companyName} - ${branchName}`.trim();
        const clients = network.clients || [];

        clients.forEach((client) => {
          rows.push({
            'Branch Network': networkLabel,
            Company: companyName,
            Branch: branchName,
            'Network Reference ID': network.reference_id || '',
            'Customer Name': client.customername || '',
            'Account Number': client.accountnumber || '',
            VLAN: client.vlan || '',
            Bandwidth:
              client.bandwidth?.description || client.bandwidth?.name || '',
            'Client ID': client._id || '',
          });
        });
      });

      if (rows.length === 0) {
        handleApiError({ message: 'No branch clients found to export' });
        return;
      }

      const filename = `all-branch-clients-${todayStamp()}.csv`;
      downloadCsv(rows, filename);
      setLastExport({ type: 'Branch Clients', count: rows.length, filename });
      message.success(`Exported ${rows.length} branch clients`);
    } catch (error) {
      handleApiError(error);
    } finally {
      setExportingClients(false);
    }
  };

  const exportAllWifiUsers = async () => {
    setExportingWifiUsers(true);
    try {
      const docs = await fetchAllWifiUsers();

      if (!docs.length) {
        handleApiError({ message: 'No wireless users found to export' });
        return;
      }

      const rows = docs.map((user) => ({
        Name: user.fullname || '',
        Username: user.username || '',
        Email: user.username || '',
        Password: user.password || '',
        Status: user.status || '',
        'User Count': user.usercount != null ? user.usercount : '',
        'Wi-Fi Group': user.wifigroup?.wifigroupname || '',
        'Branch Client': resolveWifiBranchName(user),
        'Branch Network': user.branchnetwork_name || '',
        Company: user.company?.company_name || '',
        Branch: user.branch?.branch_name || '',
        'Reference ID': user.reference_id || '',
        'User ID': user._id || '',
      }));

      const filename = `all-wireless-users-${todayStamp()}.csv`;
      downloadCsv(rows, filename);
      setLastExport({ type: 'Wireless Users', count: rows.length, filename });
      message.success(`Exported ${rows.length} wireless users`);
    } catch (error) {
      handleApiError(error);
    } finally {
      setExportingWifiUsers(false);
    }
  };

  const exportBoth = async () => {
    await exportAllBranchClients();
    await exportAllWifiUsers();
  };

  if (!isSuperAdmin) {
    return (
      <Result
        status="403"
        title="Access denied"
        subTitle="Only Super Admin can export system data."
      />
    );
  }

  const isBusy = exportingClients || exportingWifiUsers;

  return (
    <div>
      <MyBreadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Super Admin', path: '/super-admin' },
          { label: 'Data Export', path: '' },
        ]}
      />
      <PageHeader
        ghost={false}
        avatar={{ icon: <CloudDownloadOutlined /> }}
        title="System Data Export"
        subTitle="Full-system backup export — keep a current copy of every branch client and wireless user. Files download as Excel-compatible CSV."
      />

      <Card bordered style={{ marginTop: 16 }}>
        <div style={exportRowStyle}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Space align="start" size={12}>
              <TeamOutlined style={{ fontSize: 18, marginTop: 3 }} />
              <div>
                <Text strong style={{ fontSize: 15 }}>
                  Branch Clients
                </Text>
                <div>
                  <Text type="secondary">
                    All clients across every branch network, including company,
                    branch, VLAN, and bandwidth.
                  </Text>
                </div>
              </div>
            </Space>
          </div>
          <Button
            icon={<DownloadOutlined />}
            loading={exportingClients}
            disabled={isBusy && !exportingClients}
            onClick={exportAllBranchClients}
          >
            Download CSV
          </Button>
        </div>

        <Divider style={{ margin: '20px 0' }} />

        <div style={exportRowStyle}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Space align="start" size={12}>
              <WifiOutlined style={{ fontSize: 18, marginTop: 3 }} />
              <div>
                <Text strong style={{ fontSize: 15 }}>
                  Wireless Users
                </Text>
                <div>
                  <Text type="secondary">
                    All wireless users in the system, including credentials,
                    status, branch client, and branch network.
                  </Text>
                </div>
              </div>
            </Space>
          </div>
          <Button
            icon={<DownloadOutlined />}
            loading={exportingWifiUsers}
            disabled={isBusy && !exportingWifiUsers}
            onClick={exportAllWifiUsers}
          >
            Download CSV
          </Button>
        </div>

        <Divider style={{ margin: '20px 0' }} />

        <div style={exportRowStyle}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <Space align="start" size={12}>
              <FileExcelOutlined style={{ fontSize: 18, marginTop: 3 }} />
              <div>
                <Text strong style={{ fontSize: 15 }}>
                  Export Both
                </Text>
                <div>
                  <Text type="secondary">
                    Download branch clients and wireless users as two separate
                    CSV files.
                  </Text>
                </div>
              </div>
            </Space>
          </div>
          <Button
            type="primary"
            icon={<CloudDownloadOutlined />}
            loading={isBusy}
            onClick={exportBoth}
          >
            Download Both
          </Button>
        </div>

        {lastExport && (
          <>
            <Divider style={{ margin: '20px 0' }} />
            <Text type="secondary">
              Last completed: {lastExport.type} ({lastExport.count} rows) —{' '}
              {lastExport.filename}
            </Text>
          </>
        )}
      </Card>
    </div>
  );
}
